import React from 'react';
import { motion } from 'framer-motion';
import { Clock, Frown, Smartphone, MessageCircle } from 'lucide-react';
export function PainSection() {
  return <section className="py-16 px-4 bg-[#FDFBF7]" data-id="element-482">
      <div className="max-w-lg md:max-w-2xl mx-auto" data-id="element-483">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} data-id="element-484">
          <h2 className="text-2xl md:text-3xl font-bold text-[#D84315] mb-8 leading-snug" data-id="element-485">
            Para la mamá que prepara algo con amor… y termina tirándolo a la
            basura.
          </h2>

          <div className="space-y-6 text-gray-700 text-lg" data-id="element-486">
            <p data-id="element-487">Eran las 12 del mediodía.</p>
            <p data-id="element-488">
              Habías despertado temprano. Habías pensado en qué cocinar mientras
              te duchabas. Habías comprado algo más de verdura esta semana
              porque querías hacerlo mejor.
            </p>
            <p data-id="element-489">Pusiste el plato en la mesa.</p>
            <p className="font-bold text-gray-900" data-id="element-490">
              Y tu hijo lo miró como si le hubieras servido veneno.
            </p>
            <p data-id="element-491">Ese silencio. Esa carita. Ese "no quiero esto."</p>
            <p className="italic text-gray-600" data-id="element-492">
              Y ahí apareció la pregunta que más duele: ¿Estoy fallando como
              mamá?
            </p>

            <div className="py-6 flex justify-center" data-id="element-493">
              <div className="w-16 h-1 bg-[#FFCC80] rounded-full" data-id="element-494"></div>
            </div>

            <h3 className="text-xl font-bold text-gray-900" data-id="element-495">
              No estás fallando.
            </h3>
            <p data-id="element-496">
              Estás improvisando. Y la improvisación, aunque venga del mejor
              lugar del mundo, genera exactamente lo que estás viviendo: caos,
              rechazo, culpa y agotamiento.
            </p>

            <div className="bg-[#FFF3E0] p-6 rounded-2xl my-8 border border-[#FFE0B2]" data-id="element-497">
              <p className="text-xl font-bold text-[#E65100] text-center italic" data-id="element-498">
                "Tu hijo no rechaza la comida. Rechaza el caos."
              </p>
            </div>

            <p data-id="element-499">Y eso tiene solución.</p>

            <div className="py-6 flex justify-center" data-id="element-500">
              <div className="w-16 h-1 bg-[#FFCC80] rounded-full" data-id="element-501"></div>
            </div>

            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-502">
              Imagina un martes cualquiera.
            </h3>

            <div className="space-y-6" data-id="element-503">
              <div className="flex gap-4 items-start" data-id="element-504">
                <div className="bg-[#FFCC80] p-3 rounded-full shrink-0 mt-1" data-id="element-505">
                  <Clock className="w-6 h-6 text-[#E65100]" data-id="element-506" />
                </div>
                <p data-id="element-507">
                  Son las 7 AM. Hay que preparar el desayuno, llevar al cole,
                  contestar mensajes, llegar al trabajo. Ya estás en modo
                  automático.
                </p>
              </div>

              <div className="flex gap-4 items-start" data-id="element-508">
                <div className="bg-[#FFCC80] p-3 rounded-full shrink-0 mt-1" data-id="element-509">
                  <Frown className="w-6 h-6 text-[#E65100]" data-id="element-510" />
                </div>
                <p data-id="element-511">
                  A las 12 vuelves y toca cocinar. ¿Qué hay en el refrigerador?
                  Lo mismo de siempre. No hay tiempo de pensar algo "nuevo y
                  nutritivo". Pones lo que hay. Tu hijo come la mitad. O no come
                  nada. O come solo el pan. Tú insistes un poco. Él se cierra.
                  Se convierte en negociación. En pelea. En culpa.
                </p>
              </div>

              <div className="flex gap-4 items-start" data-id="element-512">
                <div className="bg-[#FFCC80] p-3 rounded-full shrink-0 mt-1" data-id="element-513">
                  <Smartphone className="w-6 h-6 text-[#E65100]" data-id="element-514" />
                </div>
                <p data-id="element-515">
                  Por la noche abres Instagram y ves a una mamá con un bowl de
                  colores perfectos, con caras sonrientes de sus hijos, y
                  piensas:{' '}
                  <span className="italic" data-id="element-516">
                    ¿Cómo lo hace ella? ¿Por qué yo no puedo?
                  </span>
                </p>
              </div>
            </div>

            <div className="mt-10 bg-white p-6 rounded-2xl shadow-sm border border-gray-100" data-id="element-517">
              <h4 className="font-bold text-gray-900 mb-4 flex items-center gap-2" data-id="element-518">
                <MessageCircle className="w-5 h-5 text-gray-400" data-id="element-519" />Y encima está
                el ruido de fondo:
              </h4>
              <ul className="space-y-3 text-gray-600" data-id="element-520">
                <li className="flex items-start gap-2" data-id="element-521">
                  <span className="text-[#FF8A65] font-bold" data-id="element-522">•</span>
                  Tu suegra que dice "antes se comía de todo sin problema"
                </li>
                <li className="flex items-start gap-2" data-id="element-523">
                  <span className="text-[#FF8A65] font-bold" data-id="element-524">•</span>
                  El pediatra que da una lista y no dice cómo
                </li>
                <li className="flex items-start gap-2" data-id="element-525">
                  <span className="text-[#FF8A65] font-bold" data-id="element-526">•</span>
                  El gurú de Instagram que hoy dice que el gluten es malo y
                  mañana que es bueno
                </li>
                <li className="flex items-start gap-2" data-id="element-527">
                  <span className="text-[#FF8A65] font-bold" data-id="element-528">•</span>
                  Tus propias dudas que no te dejan dormir
                </li>
              </ul>
            </div>

            <div className="mt-10 p-6 bg-[#FFEBEE] rounded-2xl border border-[#FFCDD2] text-center" data-id="element-529">
              <p className="text-xl font-bold text-[#C62828]" data-id="element-530">
                El resultado: comes ansiosa, cocinas con culpa, y la mesa se
                convierte en campo de batalla.
              </p>
              <p className="mt-4 text-[#C62828] font-medium" data-id="element-531">
                ¿Te suena conocido?
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}